import { useParams, useNavigate } from "react-router-dom";
import { demoQuestions } from "../../data/demoQuestions";
import { useState, useMemo } from "react";

export default function DemoContent() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);

  const data = demoQuestions[category] || [];

  const categoryTitle = useMemo(() => {
    const titles = {
      "premium-current-affairs": "Premium Current Affairs",
      "premium-question-bank": "Premium Question Banks",
      "premium-notes": "Premium Notes",
      "premium-mock": "Premium Mock Exams",
      "upsc-analysis": "Detailed UPSC Analysis",
      "ai-coaching": "AI Coaching",
      "law-structure": "Premium Law Structure",
      "law-gs1": "Premium Law GS1",
      "law-gs2": "Premium Law GS2",
      "law-gs3": "Premium Law GS3",
      "law-gs4": "Premium Law GS4",
      "ipc-sections": "Premium IPC Sections",
      "premium-prelims-polity": "Premium Prelims Polity",
      "premium-prelims-economy": "Premium Prelims Economy",
      "premium-mains-essay": "Premium Mains Essay",
      "premium-interview-guidance": "Premium Interview Guidance"
    };

    return titles[category] || "Premium Demo Content";
  }, [category]);

  const categorySubtitle = useMemo(() => {
    const subtitles = {
      "premium-current-affairs": "Daily Strategic Updates",
      "premium-question-bank": "Topic-Wise Practice",
      "premium-notes": "High Retention Notes",
      "premium-mock": "Exam Simulation",
      "upsc-analysis": "Trend Intelligence",
      "ai-coaching": "Smart Guidance",
      "law-structure": "Structured Learning",
      "law-gs1": "GS1 Law Coverage",
      "law-gs2": "GS2 Governance Focus",
      "law-gs3": "GS3 Applied Topics",
      "law-gs4": "Ethics + Case Studies",
      "ipc-sections": "Law Reference Vault",
      "premium-prelims-polity": "Polity Mastery Preview",
      "premium-prelims-economy": "Economy Concept Preview",
      "premium-mains-essay": "Essay Writing Structure",
      "premium-interview-guidance": "Board Strategy Preview"
    };

    return subtitles[category] || "Structured Premium Learning";
  }, [category]);

  return (
    <div
      className="py-5"
      style={{
        background:
          "radial-gradient(circle at top left, rgba(34,197,94,0.08), transparent 30%), radial-gradient(circle at top right, rgba(59,130,246,0.08), transparent 30%), linear-gradient(180deg, #030712 0%, #0f172a 100%)",
        minHeight: "100vh",
        color: "#ffffff"
      }}
    >
      <div className="container">
        {/* HERO */}
        <div
          className="text-center p-4 p-md-5 mb-5"
          style={{
            borderRadius: "28px",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            backdropFilter: "blur(18px)",
            boxShadow: "0 20px 80px rgba(0,0,0,0.35)"
          }}
        >
          <span
            className="d-inline-block px-3 py-2 mb-3"
            style={{
              borderRadius: "999px",
              background: "rgba(34,197,94,0.14)",
              color: "#86efac",
              fontWeight: 700,
              fontSize: "0.9rem",
              letterSpacing: "0.5px"
            }}
          >
            PREMIUM PREVIEW
          </span>

          <h1
            className="fw-bold mb-3"
            style={{
              fontSize: "clamp(2rem, 5vw, 3.4rem)",
              lineHeight: "1.15"
            }}
          >
            {categoryTitle}
          </h1>

          <p
            className="mx-auto mb-4"
            style={{
              maxWidth: "850px",
              color: "rgba(255,255,255,0.78)",
              fontSize: "1.08rem",
              lineHeight: "1.8"
            }}
          >
            {categorySubtitle} — Experience how UPSC Titan Score delivers
            structured preparation, strategic learning, and premium-quality
            guidance for serious aspirants preparing for Prelims, Mains, and
            Interview.
          </p>

          <div className="d-flex flex-wrap justify-content-center gap-3 mt-4">
            <div
              className="px-4 py-3"
              style={{
                borderRadius: "18px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                minWidth: "180px"
              }}
            >
              <h5 className="mb-1 fw-bold text-success">5+ Demo Questions</h5>
              <small style={{ color: "rgba(255,255,255,0.7)" }}>
                Preview Before Upgrade
              </small>
            </div>

            <div
              className="px-4 py-3"
              style={{
                borderRadius: "18px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                minWidth: "180px"
              }}
            >
              <h5 className="mb-1 fw-bold text-info">Premium Quality</h5>
              <small style={{ color: "rgba(255,255,255,0.7)" }}>
                Exam-Oriented Structure
              </small>
            </div>

            <div
              className="px-4 py-3"
              style={{
                borderRadius: "18px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                minWidth: "180px"
              }}
            >
              <h5 className="mb-1 fw-bold text-warning">UPSC Focused</h5>
              <small style={{ color: "rgba(255,255,255,0.7)" }}>
                Smart Preparation Flow
              </small>
            </div>
          </div>
        </div>

        {/* QUESTIONS */}
        <div className="row justify-content-center">
          <div className="col-lg-10">
            {data.length > 0 ? (
              data.map((item, index) => (
                <div
                  key={index}
                  className="mb-4"
                  style={{
                    borderRadius: "24px",
                    overflow: "hidden",
                    background: "rgba(255,255,255,0.045)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    boxShadow: "0 10px 35px rgba(0,0,0,0.22)"
                  }}
                >
                  <div
                    onClick={() => setOpen(open === index ? null : index)}
                    style={{
                      cursor: "pointer",
                      padding: "24px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "18px"
                    }}
                  >
                    <div>
                      <span
                        className="d-inline-block mb-2"
                        style={{
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          color: "#86efac",
                          letterSpacing: "0.4px"
                        }}
                      >
                        QUESTION {index + 1}
                      </span>

                      <h5
                        className="mb-0 fw-semibold"
                        style={{
                          color: "#ffffff",
                          lineHeight: "1.6"
                        }}
                      >
                        {item.q}
                      </h5>
                    </div>

                    <div
                      style={{
                        minWidth: "48px",
                        minHeight: "48px",
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        background:
                          open === index
                            ? "linear-gradient(135deg,#22c55e,#16a34a)"
                            : "rgba(255,255,255,0.06)",
                        color: "#fff",
                        fontSize: "1.3rem",
                        fontWeight: 700,
                        transition: "all 0.3s ease"
                      }}
                    >
                      {open === index ? "−" : "+"}
                    </div>
                  </div>

                  {open === index && (
                    <div
                      style={{
                        padding: "0 24px 24px 24px"
                      }}
                    >
                      <div
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(34,197,94,0.08), rgba(255,255,255,0.02))",
                          border: "1px solid rgba(34,197,94,0.15)",
                          borderRadius: "18px",
                          padding: "20px",
                          color: "rgba(255,255,255,0.88)",
                          lineHeight: "1.9",
                          fontSize: "1rem"
                        }}
                      >
                        <strong className="text-success d-block mb-2">
                          Answer:
                        </strong>
                        {item.a}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div
                className="text-center p-5"
                style={{
                  borderRadius: "24px",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)"
                }}
              >
                <h4 className="fw-bold mb-3">Demo Content Coming Soon</h4>
                <p style={{ color: "rgba(255,255,255,0.75)" }}>
                  This category preview is being prepared. Explore premium access
                  to unlock the full preparation ecosystem.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="row justify-content-center mt-5">
          <div className="col-lg-10">
            <div
              className="text-center p-4 p-md-5"
              style={{
                borderRadius: "28px",
                background:
                  "linear-gradient(135deg, rgba(34,197,94,0.14), rgba(59,130,246,0.12))",
                border: "1px solid rgba(255,255,255,0.09)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)"
              }}
            >
              <span
                className="d-inline-block px-3 py-2 mb-3"
                style={{
                  borderRadius: "999px",
                  background: "rgba(255,255,255,0.08)",
                  color: "#facc15",
                  fontWeight: 700,
                  fontSize: "0.9rem"
                }}
              >
                UPGRADE TO PREMIUM
              </span>

              <h2 className="fw-bold mb-3">
                Unlock Full UPSC Preparation Access
              </h2>

              <p
                className="mx-auto mb-4"
                style={{
                  maxWidth: "780px",
                  color: "rgba(255,255,255,0.82)",
                  lineHeight: "1.9"
                }}
              >
                Get access to complete premium content, structured modules,
                exam-oriented practice, AI guidance, law coverage, current
                affairs strategy, and high-value preparation resources designed
                to improve serious aspirant performance.
              </p>

              <div className="d-flex flex-wrap justify-content-center gap-3">
                <button
                  className="btn btn-success btn-lg px-4 py-3 fw-bold"
                  style={{
                    borderRadius: "16px",
                    boxShadow: "0 10px 30px rgba(34,197,94,0.35)"
                  }}
                  onClick={() => navigate("/subscription")}
                >
                  Unlock Full Premium Access ₹199/week
                </button>

                <button
                  className="btn btn-outline-light btn-lg px-4 py-3 fw-semibold"
                  style={{ borderRadius: "16px" }}
                  onClick={() => navigate("/")}
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SEO SUPPORT SECTION */}
        <div className="text-center mt-5 pt-3">
          <p
            style={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "0.95rem",
              lineHeight: "1.8"
            }}
          >
            UPSC Titan Score demo preview for {categoryTitle} — UPSC Prelims,
            Mains, Interview, Law GS, Current Affairs, Question Banks, Notes,
            Mock Exams, and Smart AI Preparation Modules.
          </p>
        </div>
      </div>
    </div>
  );
}